import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Crown, Ticket, ShieldCheck, TrendingUp, Wallet, Users, ChevronRight, CheckCircle2 } from "lucide-react";

export default function Landing() {
  const [inv, setInv] = useState({ total_inventory: 10000000, price_usdt: 1000, sold: 0, available: 10000000 });

  useEffect(() => {
    // fetch inventory once on mount; api and setInv are stable references
    api.get("/public/inventory").then(({ data }) => setInv(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#0A1628]">
      <TopBar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-40 pointer-events-none" />
        <div className="mx-auto max-w-7xl px-6 pt-16 pb-24 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="overline mb-4"><span className="mr-2">◆</span>Crown Membership · BNB Smart Chain</div>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-white">
                A premium coupon<br/>
                <span className="text-[#556B2F]">for the </span>
                <span className="gold-underline text-[#D4AF37]">Crown</span>
                <span className="text-[#556B2F]"> circle.</span>
              </h1>
              <p className="mt-6 max-w-xl text-slate-300 text-lg leading-relaxed">
                Crown Coupon is a numbered, blockchain-verified benefit pass. One coupon, one identity — issued on BNB Smart Chain, purchased with USDT, and earning legitimate referral rewards on real qualifying sales.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register"><Button className="h-12 px-7 bg-[#556B2F] hover:bg-[#2A331A] text-white text-base" data-testid="hero-cta-register">Claim your Crown Coupon<ChevronRight className="ml-1 h-4 w-4" /></Button></Link>
                <Link to="/login"><Button variant="outline" className="h-12 px-6 text-base border-[#556B2F]/30" data-testid="hero-cta-login">Sign in</Button></Link>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-300">
                <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#556B2F]" /> USDT BEP-20 only</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#556B2F]" /> On-chain verified</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#556B2F]" /> No custody · no keys stored</div>
              </div>
            </div>

            <div className="lg:col-span-5" id="coupon">
              <div className="crown-card p-8 relative">
                <div className="absolute -top-3 left-8 chip bg-[#3B4A1F] text-white" data-testid="chip-inventory">Live Inventory</div>
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-md bg-[#2A331A]">
                    <Crown className="h-6 w-6 text-[#D4AF37]" />
                  </span>
                  <div>
                    <div className="overline">Crown Coupon</div>
                    <div className="font-serif text-3xl leading-none">1,000 <span className="text-[#D4AF37]">USDT</span></div>
                  </div>
                </div>
                <div className="mt-8 grid grid-cols-2 gap-6">
                  <div>
                    <div className="overline">Available</div>
                    <div className="mt-1 font-serif text-3xl tabular" data-testid="stat-available">{inv.available.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="overline">Sold</div>
                    <div className="mt-1 font-serif text-3xl tabular" data-testid="stat-sold">{inv.sold.toLocaleString()}</div>
                  </div>
                </div>
                <div className="mt-6 h-2 rounded-full bg-[#1B3455]/60 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#556B2F] to-[#D4AF37]" style={{ width: `${Math.min(100, (inv.sold / Math.max(1, inv.total_inventory)) * 100)}%` }} />
                </div>
                <div className="mt-6 text-xs text-slate-400">Total minted supply — <span className="tabular">{inv.total_inventory.toLocaleString()}</span> coupons. Every issuance is coupled with an on-chain transaction hash.</div>
                <Link to="/register" className="block mt-6">
                  <Button className="w-full h-11 bg-[#3B4A1F] hover:bg-[#212814] text-white" data-testid="coupon-cta-buy">Buy Crown Coupon</Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bento features */}
      <section id="how" className="mx-auto max-w-7xl px-6 py-24">
        <div className="max-w-2xl mb-14">
          <div className="overline mb-3">How it works</div>
          <h2 className="font-serif text-4xl sm:text-5xl text-white">From wallet to Crown, in three signed steps.</h2>
        </div>

        <div className="grid md:grid-cols-12 gap-6">
          <div className="crown-card md:col-span-7 p-8">
            <Ticket className="h-8 w-8 text-[#556B2F]" />
            <h3 className="mt-4 font-serif text-2xl">01 · Register & receive your Referral ID</h3>
            <p className="mt-2 text-slate-300 max-w-xl">Create an account, get a unique User ID and Referral link. Bring friends and earn real commissions only on genuine, completed sales — never on sign-ups.</p>
          </div>
          <div className="crown-card md:col-span-5 p-8">
            <Wallet className="h-8 w-8 text-[#D4AF37]" />
            <h3 className="mt-4 font-serif text-2xl">02 · Pay with USDT BEP-20</h3>
            <p className="mt-2 text-slate-300">Connect your BSC wallet, review the transaction, and pay 1,000 USDT. We never ask for your seed phrase or private key.</p>
          </div>
          <div className="crown-card md:col-span-5 p-8">
            <ShieldCheck className="h-8 w-8 text-[#556B2F]" />
            <h3 className="mt-4 font-serif text-2xl">03 · On-chain verification</h3>
            <p className="mt-2 text-slate-300">Your transaction is verified against the receiving wallet, contract, and amount. Only then is a Crown Coupon issued to your account.</p>
          </div>
          <div className="crown-card md:col-span-7 p-8 bg-[#2A331A] text-white border-transparent">
            <TrendingUp className="h-8 w-8 text-[#D4AF37]" />
            <h3 className="mt-4 font-serif text-2xl text-white">04 · Earn on real qualifying sales</h3>
            <p className="mt-2 text-slate-300 max-w-xl">Three-level referral commissions — configured by the admin, approved after successful settlement, withdrawable in USDT to your BEP-20 wallet.</p>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-[#132741] border-y border-[#556B2F]/10">
        <div className="mx-auto max-w-7xl px-6 py-14 grid md:grid-cols-4 gap-8">
          {[
            { icon: <ShieldCheck className="h-6 w-6" />, t: "Non-custodial", d: "We store only the receiving public address." },
            { icon: <Users className="h-6 w-6" />, t: "3-Level referrals", d: "Legitimate commissions on real completed sales." },
            { icon: <Crown className="h-6 w-6" />, t: "Numbered coupons", d: "CC-00000001 to CC-10000000 — provable, unique." },
            { icon: <Wallet className="h-6 w-6" />, t: "USDT withdrawals", d: "Approved commissions payable in USDT BEP-20." },
          ].map((f) => (
            <div key={f.t} className="flex items-start gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[#556B2F]/10 text-[#556B2F]">{f.icon}</span>
              <div>
                <div className="font-semibold text-white">{f.t}</div>
                <div className="text-sm text-slate-300 mt-1">{f.d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="mx-auto max-w-4xl px-6 py-24">
        <div className="overline mb-3">FAQ</div>
        <h2 className="font-serif text-4xl">The essentials.</h2>
        <div className="mt-10 space-y-6">
          {[
            { q: "Is this an investment product?", a: "No. A Crown Coupon is a numbered membership pass tied to product/service benefits and a referral program. There are no guaranteed returns, ROIs or daily earnings." },
            { q: "Which network and token do you accept?", a: "Only USDT (BEP-20) on BNB Smart Chain. No other cryptocurrency or fiat is supported at this time." },
            { q: "How does payment verification work?", a: "Payments are matched against the receiving wallet, contract, and exact amount on-chain, and confirmed with the required confirmations before the coupon is issued." },
            { q: "What information do you ever ask from my wallet?", a: "Only your public wallet address for reference. We never request seed phrases, private keys, or wallet passwords." },
          ].map((f) => (
            <div key={f.q} className="crown-card p-6">
              <div className="font-semibold text-white">{f.q}</div>
              <p className="mt-2 text-slate-300 text-sm leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-[#556B2F]/10 py-10">
        <div className="mx-auto max-w-7xl px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-slate-400">© {new Date().getFullYear()} Crown Coupon. All rights reserved.</div>
          <div className="text-xs text-slate-400">Demo mode enabled — payments simulated in this environment.</div>
        </div>
      </footer>
    </div>
  );
}
